import React from "react";
import { Card, CardContent, Typography, Button, Chip } from "@mui/material";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MailOutline as MailOutlineIcon,
  Event as EventIcon,
  Comment as CommentIcon,
  NoteAdd as NoteAddIcon,
  AddTask as AddTaskIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";
import { useUser } from "../../../context/UserContext";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import AddCommunicationNote from "./AddCommunicationNote";
import { createLeadNote } from "../../../services/lead.service";

const getStatusColor = (status: string) => {
  switch (status) {
    case "Pending":
      return "warning";
    case "Public Lead":
      return "secondary";
    case "Invited":
      return "info";
    case "Registered":
      return "success";
    default:
      return "default";
  }
};

interface LeadCardViewProps {
  leads: any[];
  onEdit: (lead: any) => void;
  onDelete: (lead: any) => void;
  onInvite: (lead: any) => void;
  onConvert: (lead: any) => void;
  onAddTask: (lead: any) => void;
}

const LeadCardView: React.FC<LeadCardViewProps> = ({
  leads,
  onEdit,
  onDelete,
  onInvite,
  onConvert,
  onAddTask
}) => {
  const { user } = useUser();
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState<any>(null);

  // Note dialog state
  const [openNoteDialog, setOpenNoteDialog] = React.useState(false);
  const [activeLead, setActiveLead] = React.useState<any>(null);

  const handleDeleteConfirm = () => {
    if (selectedLead) {
      onDelete(selectedLead);
    }
    setOpenDeleteDialog(false);
    setSelectedLead(null);
  };

  const openDeleteDialogHandler = (lead: any) => {
    setSelectedLead(lead);
    setOpenDeleteDialog(true);
  };

  const handleAddNote = (lead: any) => {
    setActiveLead(lead);
    setOpenNoteDialog(true);
  };

  const handleSaveNote = async (note: any) => {
    try {
      if (!activeLead) return;
      await createLeadNote(activeLead.id, note);
      // Note: In a real implementation, you'd want to refresh the leads data
      // For now, we'll just close the dialog
      setOpenNoteDialog(false);
      setActiveLead(null);
    } catch (error) {
      console.error("Error creating note:", error);
    }
  };
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 pb-6">
        {leads.map((lead) => {
          const lastSummary = lead?.notes?.[0]?.summary ?? null;
          const createdByName =
            `${(lead.createdByUser as any)?.firstName || ''} ${(lead.createdByUser as any)?.middleName || ''} ${(lead.createdByUser as any)?.lastName || ''}`.trim() ||
            `${(lead.createdByBusinessUser as any)?.firstName || ''} ${(lead.createdByBusinessUser as any)?.middleName || ''} ${(lead.createdByBusinessUser as any)?.lastName || ''}`.trim() || "-";

          return (
            <Card key={lead.id} className="rounded-2xl shadow border border-gray-200">
              <CardContent className="flex flex-col items-center text-center px-4 py-6 gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center shadow">
                  <PersonIcon className="text-gray-400 text-6xl" />
                </div>

                <Typography variant="h6" className="font-semibold text-gray-900">
                  {[lead.firstName, lead.middleName, lead.lastName].filter(Boolean).join(" ") || "-"}
                </Typography>

                <div className="text-sm text-gray-700 space-y-2 text-left w-full">
                  <div className="flex items-center gap-2">
                    <BusinessIcon fontSize="small" />
                    <span>
                      <strong>Business:</strong> {lead.businessName || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EmailIcon fontSize="small" />
                    <span>
                      <strong>Email:</strong> {lead.email || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon fontSize="small" />
                    <span>
                      <strong>Phone:</strong> {lead.phone ? formatPhoneNumber(lead.phone) : "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <EventIcon fontSize="small" />
                    <span>
                      <strong>Follow Up:</strong>{" "}
                      {lead.nextFollowUpDate
                        ? format(new Date(lead.nextFollowUpDate), "MM-dd-yyyy")
                        : "-"}
                    </span>
                  </div>
                  {(lastSummary || lead.comments) && (
                    <div className="flex items-center gap-2">
                      <CommentIcon fontSize="small" />
                      <span>
                        <strong>Comments:</strong> {lastSummary || lead.comments || "-"}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 font-medium">Status:</span>
                    <Chip
                      label={lead.status || "-"}
                      color={getStatusColor(lead.status)}
                      variant="filled"
                      size="small"
                    />
                  </div>
                  {user?.role !== "EMPLOYEE" && (
                    <div className="flex items-center gap-2">
                    <span className="text-gray-600 font-medium">Created by: {createdByName}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 w-full mt-4">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    color="secondary"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onEdit(lead)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<NoteAddIcon />}
                    color="info"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => handleAddNote(lead)}
                  >
                    Note
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddTaskIcon />}
                    color="info"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onAddTask(lead)}
                  >
                    Task
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<MailOutlineIcon />}
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onInvite(lead)}
                  >
                    Invite
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    color="success"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onConvert(lead)}
                  >
                    Convert
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeleteDialogHandler(lead)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={[selectedLead?.firstName, selectedLead?.middleName, selectedLead?.lastName].filter(Boolean).join(" ") || ""}
      />

      {/* Add Note Dialog */}
      <AddCommunicationNote
        open={openNoteDialog}
        onClose={() => {
          setOpenNoteDialog(false);
          setActiveLead(null);
        }}
        onSave={handleSaveNote}
        note={null}
      />
    </>
  );
};

export default LeadCardView;
